import { useState, useEffect, useCallback } from "react";
import {
  fetchDiffVersions,
  fetchDiffTree,
  fetchActiveVersion,
  revertDiffField,
  revertAllDiff,
} from "../../../api/clientV2";
import type { DiffVersion, TreeDiff } from "../../../models";

export function useDiff() {
  const [versions, setVersions] = useState<DiffVersion[]>([]);
  const [oldVer, setOldVer] = useState<string | null>(null);
  const [newVer, setNewVer] = useState<string | null>(null);
  const [diff, setDiff] = useState<TreeDiff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reverting, setReverting] = useState<string | null>(null);
  const [revertingAll, setRevertingAll] = useState(false);

  // Load available versions on mount — auto-select original vs working for active version
  useEffect(() => {
    Promise.all([fetchDiffVersions(), fetchActiveVersion()])
      .then(([v, active]) => {
        setVersions(v);
        const n = active.version;
        const hasOriginal = v.some((e) => e.id === `${n}_original`);
        const hasWorking = v.some((e) => e.id === String(n));
        if (hasOriginal && hasWorking) {
          setOldVer(`${n}_original`);
          setNewVer(String(n));
        }
      })
      .catch(() => setError("Failed to load versions"));
  }, []);

  // Fetch diff when versions are selected or filter changes
  const loadDiff = useCallback(async () => {
    if (oldVer === null || newVer === null) return;
    setLoading(true);
    setError(null);
    try {
      const result = await fetchDiffTree(oldVer, newVer);
      setDiff(result);
    } catch {
      setError("Failed to load diff");
      setDiff(null);
    } finally {
      setLoading(false);
    }
  }, [oldVer, newVer]);

  useEffect(() => {
    loadDiff();
  }, [loadDiff]);

  const canRevert = newVer != null && !newVer.includes("_original");

  const revertField = useCallback(async (taskCode: string, field: string) => {
    if (!oldVer || !newVer) return;
    setReverting(`${taskCode}:${field}`);
    try {
      await revertDiffField(oldVer, newVer, taskCode, [field]);
      await loadDiff();
    } catch {
      setError("Revert failed");
    } finally {
      setReverting(null);
    }
  }, [oldVer, newVer, loadDiff]);

  const revertActivity = useCallback(async (taskCode: string) => {
    if (!oldVer || !newVer) return;
    setReverting(taskCode);
    try {
      await revertDiffField(oldVer, newVer, taskCode);
      await loadDiff();
    } catch {
      setError("Revert failed");
    } finally {
      setReverting(null);
    }
  }, [oldVer, newVer, loadDiff]);

  const revertAll = useCallback(async () => {
    if (!oldVer || !newVer) return;
    setRevertingAll(true);
    try {
      await revertAllDiff(oldVer, newVer);
      await loadDiff();
    } catch {
      setError("Revert all failed");
      throw new Error("Revert all failed");
    } finally {
      setRevertingAll(false);
    }
  }, [oldVer, newVer, loadDiff]);

  return {
    versions, oldVer, newVer, setOldVer, setNewVer,
    diff, loading, error,
    canRevert, reverting, revertField, revertActivity,
    revertingAll, revertAll,
  };
}
